import QRCode from 'qrcode';
import { InventoryQRData } from '../components/InventoryQRScannerDialog';

export interface InventoryQRPrintService {
  printQRCode(
    inventoryId: string,
    inventoryCode: string,
    inventoryName: string,
    options?: QRPrintOptions
  ): Promise<void>;
  generatePrintableQRCode(
    inventoryId: string,
    inventoryCode: string,
    inventoryName: string,
    options?: QRPrintOptions
  ): Promise<string>;
}

export interface QRPrintOptions {
  size?: number;
  includeLabel?: boolean;
  includeCode?: boolean;
  includeName?: boolean;
  labelText?: string;
  fontSize?: number;
  margin?: number;
}

export class InventoryQRPrintServiceImpl implements InventoryQRPrintService {
  async printQRCode(
    inventoryId: string,
    inventoryCode: string,
    inventoryName: string,
    options: QRPrintOptions = {}
  ): Promise<void> {
    try {
      const printableHTML = await this.generatePrintableQRCode(
        inventoryId,
        inventoryCode,
        inventoryName,
        options
      );

      // Create a new window for printing
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      if (!printWindow) {
        throw new Error('Unable to open print window');
      }

      printWindow.document.write(printableHTML);
      printWindow.document.close();

      // Wait for the content to load, then print
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };
    } catch (error) {
      console.error('Failed to print QR code:', error);
      throw new Error('QR code printing failed');
    }
  }

  async generatePrintableQRCode(
    inventoryId: string,
    inventoryCode: string,
    inventoryName: string,
    options: QRPrintOptions = {}
  ): Promise<string> {
    const {
      size = 200,
      includeLabel = true,
      includeCode = true,
      includeName = true,
      labelText = 'Inventory QR Code',
      fontSize = 14,
      margin = 20,
    } = options;

    const qrData: InventoryQRData = {
      type: 'INVENTORY',
      inventoryId,
      inventoryCode,
      inventoryName,
      timestamp: Date.now(),
    };

    try {
      // Generate QR code as SVG
      const qrCodeSVG = await QRCode.toString(JSON.stringify(qrData), {
        type: 'svg',
        width: size,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      });

      // Create printable HTML
      const printableHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Inventory QR Code - ${inventoryCode}</title>
          <style>
            @media print {
              body { margin: 0; }
              .print-container { 
                display: flex; 
                flex-direction: column; 
                align-items: center; 
                justify-content: center; 
                min-height: 100vh; 
                padding: ${margin}px;
                font-family: Arial, sans-serif;
              }
              .qr-code { 
                margin: 20px 0; 
              }
              .label { 
                font-size: ${fontSize + 4}px; 
                font-weight: bold; 
                margin-bottom: 10px; 
                text-align: center;
              }
              .code { 
                font-size: ${fontSize}px; 
                margin: 5px 0; 
                text-align: center;
                font-weight: bold;
              }
              .name { 
                font-size: ${fontSize - 2}px; 
                margin: 5px 0; 
                text-align: center;
                color: #666;
              }
              .timestamp {
                font-size: ${fontSize - 4}px;
                margin-top: 10px;
                text-align: center;
                color: #999;
              }
            }
            @media screen {
              body { margin: 20px; }
              .print-container { 
                display: flex; 
                flex-direction: column; 
                align-items: center; 
                justify-content: center; 
                min-height: 80vh; 
                padding: ${margin}px;
                font-family: Arial, sans-serif;
                border: 1px solid #ccc;
                border-radius: 8px;
              }
              .qr-code { 
                margin: 20px 0; 
              }
              .label { 
                font-size: ${fontSize + 4}px; 
                font-weight: bold; 
                margin-bottom: 10px; 
                text-align: center;
              }
              .code { 
                font-size: ${fontSize}px; 
                margin: 5px 0; 
                text-align: center;
                font-weight: bold;
              }
              .name { 
                font-size: ${fontSize - 2}px; 
                margin: 5px 0; 
                text-align: center;
                color: #666;
              }
              .timestamp {
                font-size: ${fontSize - 4}px;
                margin-top: 10px;
                text-align: center;
                color: #999;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${includeLabel ? `<div class="label">${labelText}</div>` : ''}
            <div class="qr-code">${qrCodeSVG}</div>
            ${
              includeCode
                ? `<div class="code">Code: ${inventoryCode}</div>`
                : ''
            }
            ${includeName ? `<div class="name">${inventoryName}</div>` : ''}
            <div class="timestamp">Generated: ${new Date().toLocaleString()}</div>
          </div>
        </body>
        </html>
      `;

      return printableHTML;
    } catch (error) {
      console.error('Failed to generate printable QR code:', error);
      throw new Error('Printable QR code generation failed');
    }
  }

  // Generate QR code for multiple inventory items (batch printing)
  async generateBatchPrintableQRCode(
    items: Array<{
      inventoryId: string;
      inventoryCode: string;
      inventoryName: string;
    }>,
    options: QRPrintOptions = {}
  ): Promise<string> {
    const {
      size = 150,
      includeLabel = true,
      includeCode = true,
      includeName = true,
      labelText = 'Inventory QR Codes',
      fontSize = 12,
      margin = 20,
    } = options;

    try {
      const qrCodes = await Promise.all(
        items.map(async (item) => {
          const qrData: InventoryQRData = {
            type: 'INVENTORY',
            inventoryId: item.inventoryId,
            inventoryCode: item.inventoryCode,
            inventoryName: item.inventoryName,
            timestamp: Date.now(),
          };

          const qrCodeSVG = await QRCode.toString(JSON.stringify(qrData), {
            type: 'svg',
            width: size,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#FFFFFF',
            },
          });

          return {
            ...item,
            qrCodeSVG,
          };
        })
      );

      // Create printable HTML for batch printing
      const printableHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Batch Inventory QR Codes</title>
          <style>
            @media print {
              body { margin: 0; }
              .print-container { 
                padding: ${margin}px;
                font-family: Arial, sans-serif;
              }
              .batch-header {
                text-align: center;
                margin-bottom: 30px;
                font-size: ${fontSize + 6}px;
                font-weight: bold;
              }
              .qr-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 20px;
                align-items: start;
              }
              .qr-item {
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                page-break-inside: avoid;
              }
              .qr-code { 
                margin: 10px 0; 
              }
              .code { 
                font-size: ${fontSize}px; 
                margin: 5px 0; 
                font-weight: bold;
              }
              .name { 
                font-size: ${fontSize - 2}px; 
                margin: 5px 0; 
                color: #666;
                word-wrap: break-word;
              }
            }
            @media screen {
              body { margin: 20px; }
              .print-container { 
                padding: ${margin}px;
                font-family: Arial, sans-serif;
                border: 1px solid #ccc;
                border-radius: 8px;
              }
              .batch-header {
                text-align: center;
                margin-bottom: 30px;
                font-size: ${fontSize + 6}px;
                font-weight: bold;
              }
              .qr-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 20px;
                align-items: start;
              }
              .qr-item {
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                padding: 10px;
                border: 1px solid #eee;
                border-radius: 4px;
              }
              .qr-code { 
                margin: 10px 0; 
              }
              .code { 
                font-size: ${fontSize}px; 
                margin: 5px 0; 
                font-weight: bold;
              }
              .name { 
                font-size: ${fontSize - 2}px; 
                margin: 5px 0; 
                color: #666;
                word-wrap: break-word;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="batch-header">${labelText}</div>
            <div class="qr-grid">
              ${qrCodes
                .map(
                  (item) => `
                <div class="qr-item">
                  <div class="qr-code">${item.qrCodeSVG}</div>
                  ${
                    includeCode
                      ? `<div class="code">${item.inventoryCode}</div>`
                      : ''
                  }
                  ${
                    includeName
                      ? `<div class="name">${item.inventoryName}</div>`
                      : ''
                  }
                </div>
              `
                )
                .join('')}
            </div>
            <div style="text-align: center; margin-top: 30px; font-size: ${
              fontSize - 4
            }px; color: #999;">
              Generated: ${new Date().toLocaleString()} | Total Items: ${
        items.length
      }
            </div>
          </div>
        </body>
        </html>
      `;

      return printableHTML;
    } catch (error) {
      console.error('Failed to generate batch printable QR codes:', error);
      throw new Error('Batch printable QR code generation failed');
    }
  }
}

// Export singleton instance
export const inventoryQRPrintService = new InventoryQRPrintServiceImpl();
